import {get} from 'lodash'
import {delay} from 'utils/delay'
import {reportRecaptchaFailure} from './reportRecaptchaFailure'
import {CreateRecaptchaToken, RecaptchaAction} from './types'

export const RECAPTCHA_REQUEST_ATTEMPTS = 3
export const RECAPTCHA_NULL_TOKEN_RETRY_DELAY_MS = 2_000

/**
 * The retriable condition is returned only in `errorCode`. The optional `reason`
 * (`MISSING`, `EXPIRED`, `DUPE`, `BROWSER_ERROR`) is diagnostic only - every reason
 * gets the same fresh-token retry, so it must not gate the decision.
 */
export const isRecaptchaRequired = (error: unknown) => {
  return get(error, 'response.data.errorCode') === 'RECAPTCHA_REQUIRED'
}

/**
 * Runs a protected Checkout API request with a freshly generated token, retrying with
 * a new token whenever Ventrata answers `RECAPTCHA_REQUIRED`.
 *
 * When the loader or token generation fails, the request is still sent with `null` so
 * that Ventrata can return the explicit retryable error, then a short wait precedes a
 * fresh loader/token attempt - which may adopt the existing script rather than
 * downloading it again.
 *
 * `VERIFICATION_FAILED` and `PAYMENT_DECLINE_LIMIT_EXCEEDED` are not retriable and
 * propagate on the first response.
 */
export const withRecaptchaRetry = async <T>(
  action: RecaptchaAction,
  createToken: CreateRecaptchaToken,
  sendRequest: (token: string | null) => Promise<T>
): Promise<T> => {
  let lastError: unknown

  for (let attempt = 1; attempt <= RECAPTCHA_REQUEST_ATTEMPTS; attempt++) {
    let token: string | null = null

    try {
      token = await createToken(action)
    } catch (error) {
      reportRecaptchaFailure('recaptcha.request_token_unavailable', error, {
        action,
        requestAttempt: attempt,
      })
    }

    try {
      return await sendRequest(token)
    } catch (error) {
      lastError = error

      if (!isRecaptchaRequired(error) || attempt === RECAPTCHA_REQUEST_ATTEMPTS) {
        throw error
      }

      if (token === null) {
        await delay(RECAPTCHA_NULL_TOKEN_RETRY_DELAY_MS)
      }
    }
  }

  throw lastError
}
